import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';

const PriceChart = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('6M');
  const [selectedCategory, setSelectedCategory] = useState('watches');
  const [chartType, setChartType] = useState('line');

  const timePeriods = [
    { id: '1M', label: '1M', active: false },
    { id: '3M', label: '3M', active: false },
    { id: '6M', label: '6M', active: true },
    { id: '1Y', label: '1Y', active: false },
    { id: '2Y', label: '2Y', active: false },
    { id: 'ALL', label: 'All', active: false }
  ];

  const categories = [
    { id: 'watches', label: 'Vintage Watches', color: '#D4AF37' },
    { id: 'cards', label: 'Sports Cards', color: '#3B82F6' },
    { id: 'comics', label: 'Comic Books', color: '#8B5CF6' },
    { id: 'art', label: 'Art & Antiques', color: '#10B981' }
  ];

  const priceData = [
    { date: 'Apr 2024', watches: 85000, cards: 45000, comics: 32000, art: 78000 },
    { date: 'May 2024', watches: 88000, cards: 48000, comics: 35000, art: 82000 },
    { date: 'Jun 2024', watches: 92000, cards: 52000, comics: 38000, art: 85000 },
    { date: 'Jul 2024', watches: 89000, cards: 49000, comics: 36000, art: 88000 },
    { date: 'Aug 2024', watches: 95000, cards: 55000, comics: 42000, art: 92000 },
    { date: 'Sep 2024', watches: 98000, cards: 58000, comics: 45000, art: 95000 },
    { date: 'Oct 2024', watches: 102000, cards: 62000, comics: 48000, art: 98000 }
  ];

  const currentCategory = categories?.find(cat => cat?.id === selectedCategory);
  const currentValue = priceData?.[priceData?.length - 1]?.[selectedCategory];
  const previousValue = priceData?.[priceData?.length - 2]?.[selectedCategory];
  const changePercent = ((currentValue - previousValue) / previousValue * 100)?.toFixed(1);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-popover-foreground mb-2">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              ></div>
              <span className="text-sm text-popover-foreground">
                ${entry?.value?.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-xl font-bold text-card-foreground">Price Trends</h2>
          <p className="text-sm text-muted-foreground">Historical price analysis and market trends</p>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Chart Type Toggle */}
          <div className="flex items-center bg-muted/30 rounded-lg p-1">
            <button
              onClick={() => setChartType('line')}
              className={`p-2 rounded-md transition-colors duration-200 ${
                chartType === 'line' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-card-foreground'
              }`}
            >
              <Icon name="TrendingUp" size={16} />
            </button>
            <button
              onClick={() => setChartType('area')}
              className={`p-2 rounded-md transition-colors duration-200 ${
                chartType === 'area' ?'bg-primary text-primary-foreground' :'text-muted-foreground hover:text-card-foreground'
              }`}
            >
              <Icon name="AreaChart" size={16} />
            </button>
          </div>

          {/* Export Button */}
          <button className="flex items-center space-x-2 px-3 py-2 bg-muted/50 text-muted-foreground rounded-lg hover:bg-muted hover:text-card-foreground transition-colors duration-200">
            <Icon name="Download" size={16} />
            <span className="text-sm">Export</span>
          </button>
        </div>
      </div>
      {/* Category Selection */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories?.map((category) => (
          <button
            key={category?.id}
            onClick={() => setSelectedCategory(category?.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              selectedCategory === category?.id
                ? 'bg-primary/10 text-primary border border-primary/20' :'bg-muted/30 text-muted-foreground hover:bg-muted/50 hover:text-card-foreground'
            }`}
          >
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: category?.color }}
            ></div>
            <span>{category?.label}</span>
          </button>
        ))}
      </div>
      {/* Current Value Display */}
      <div className="flex items-center justify-between mb-6 p-4 bg-muted/20 rounded-lg">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">Current Average Price</h3>
          <p className="text-2xl font-bold text-card-foreground">
            ${currentValue?.toLocaleString()}
          </p>
        </div>
        <div className="text-right">
          <div className={`flex items-center space-x-1 text-sm font-medium ${
            parseFloat(changePercent) >= 0 ? 'text-success' : 'text-destructive'
          }`}>
            <Icon 
              name={parseFloat(changePercent) >= 0 ? 'TrendingUp' : 'TrendingDown'} 
              size={16} 
            />
            <span>{changePercent}%</span>
          </div>
          <p className="text-xs text-muted-foreground">vs last month</p>
        </div>
      </div>
      {/* Time Period Selection */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center bg-muted/30 rounded-lg p-1">
          {timePeriods?.map((period) => (
            <button
              key={period?.id}
              onClick={() => setSelectedPeriod(period?.id)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200 ${
                selectedPeriod === period?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-card-foreground'
              }`}
            >
              {period?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={priceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="date" 
                stroke="#A0A3B1"
                fontSize={12}
              />
              <YAxis 
                stroke="#A0A3B1"
                fontSize={12}
                tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey={selectedCategory}
                stroke={currentCategory?.color}
                strokeWidth={3}
                dot={{ fill: currentCategory?.color, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: currentCategory?.color, strokeWidth: 2 }}
              />
            </LineChart>
          ) : (
            <AreaChart data={priceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="date" 
                stroke="#A0A3B1"
                fontSize={12}
              />
              <YAxis 
                stroke="#A0A3B1"
                fontSize={12}
                tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey={selectedCategory}
                stroke={currentCategory?.color}
                fill={`${currentCategory?.color}20`}
                strokeWidth={2}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PriceChart;